/**
 * FleetManager V3 - Deadlines Widget
 * Upcoming deadlines and reminders - clickable items navigate to relevant views
 *
 * i18n : `item.type` est un CODE (ct, insurance, license, maintenance ou un
 * code de maintenance_request.type) traduit via dashboard.deadlines.types.*.
 * `item.plateKey` (ex. dashboard.deadlines.driver) remplace `item.plate`
 * quand la seconde ligne est un libellé et non une donnée.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../../shared/index.js';

// Keys are the type codes produced by useDashboardData.js deadlines.
const TYPE_STYLES = {
  ct: 'text-amber-400 bg-amber-500/20',
  insurance: 'text-blue-400 bg-blue-500/20',
  license: 'text-purple-400 bg-purple-500/20',
  // Maintenance sub-types
  oil_change: 'text-emerald-400 bg-emerald-500/20',
  tire_rotation: 'text-emerald-400 bg-emerald-500/20',
  brake_service: 'text-emerald-400 bg-emerald-500/20',
  engine_repair: 'text-orange-400 bg-orange-500/20',
  transmission: 'text-orange-400 bg-orange-500/20',
  electrical: 'text-yellow-400 bg-yellow-500/20',
  bodywork: 'text-sky-400 bg-sky-500/20',
  inspection: 'text-red-400 bg-red-500/20',
  emergency: 'text-red-400 bg-red-500/20',
  other: 'text-slate-400 bg-slate-500/20',
  maintenance: 'text-emerald-400 bg-emerald-500/20',
};

// Map deadline types to navigation targets.
const TYPE_NAVIGATION = {
  ct: 'inspections',
  insurance: 'insurance',
  license: 'drivers',
  // Everything else goes to the maintenance view — fallback also targets it.
};

function daysLeftLabel(t, daysLeft) {
  if (daysLeft === 0) return t('dashboard.deadlines.today');
  if (daysLeft === 1) return t('dashboard.deadlines.tomorrow');
  return t('dashboard.deadlines.days', { count: daysLeft });
}

function DeadlineItem({ item, onNavigate }) {
  const { t } = useTranslation();
  const typeStyle = TYPE_STYLES[item.type] || 'text-gray-500 dark:text-slate-400 bg-gray-200 dark:bg-slate-500/20';
  const targetView = TYPE_NAVIGATION[item.type] || 'maintenance';
  // Compat : un type inconnu (ancien libellé FR en clair) est affiché tel quel.
  const typeLabel = t(`dashboard.deadlines.types.${item.type}`, { defaultValue: item.type });
  const secondLine = item.plateKey ? t(item.plateKey) : item.plate;

  const handleClick = (e) => {
    e.stopPropagation();
    onNavigate?.(targetView, { vehicleId: item.vehicleId });
  };

  return (
    <div
      onClick={handleClick}
      className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group"
    >
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeStyle}`}>
        {typeLabel}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-900 dark:text-white truncate">{item.vehicle}</div>
        <div className="text-xs text-gray-500 dark:text-slate-500">{secondLine}</div>
      </div>
      {item.daysLeft !== undefined && (
        <span className={`text-xs ${item.daysLeft <= 7 ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-slate-400'}`}>
          {daysLeftLabel(t, item.daysLeft)}
        </span>
      )}
      <ChevronRight size={14} className="text-gray-400 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

export function DeadlinesWidget({ deadlines = [], onNavigate }) {
  const { t } = useTranslation();
  const handleViewAll = () => {
    onNavigate?.('maintenance');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Calendar} iconColor="text-amber-400">{t('dashboard.deadlines.title')}</CardTitle>
        {deadlines.length > 0 && (
          <span className="text-xs text-gray-500 dark:text-slate-400">{t('dashboard.deadlines.upcoming', { count: deadlines.length })}</span>
        )}
      </CardHeader>

      {deadlines.length > 0 ? (
        <div className="space-y-1">
          {deadlines.slice(0, 5).map(item => (
            <DeadlineItem key={item.id} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      ) : (
        <div className="text-center py-4">
          <div className="text-sm text-gray-500 dark:text-slate-400">{t('dashboard.deadlines.empty')}</div>
        </div>
      )}

      {deadlines.length > 5 && (
        <button
          onClick={handleViewAll}
          className="w-full mt-2 py-2 text-xs text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          {t('dashboard.deadlines.viewAll', { count: deadlines.length })}
          <ChevronRight size={12} />
        </button>
      )}
    </Card>
  );
}

export default DeadlinesWidget;
